import { useRoute } from "vue-router";
import { toast } from "vue-sonner";
import { useSurveyStore } from "@stores/survey/survey";

interface SideStore {
  hasUnsavedChanges: () => boolean;
  refresh: () => Promise<void> | void;
}

interface SideStoreEntry {
  routes: string[];
  touchedBy: (tool: string) => boolean;
  /** True when the store owns its data entirely, so the survey refetch can be skipped. */
  standalone: boolean;
  load: () => Promise<SideStore>;
}

export function useAssistantPageRefresh() {
  const route = useRoute();

  const SIDE_STORES: SideStoreEntry[] = [
    {
      routes: ["SurveyResultsV2", "SurveyResultsV2View"],
      touchedBy: (tool) => tool.includes("dashboard"),
      // Dashboards are independent of the survey store, so its refetch — the
      // expensive half — can be skipped when nothing else changed.
      standalone: true,
      load: async () => {
        const { useResultsV2Store } = await import("@stores/survey/resultsV2/resultsV2");
        const store = useResultsV2Store();

        return {
          hasUnsavedChanges: () => store.hasUnsavedChanges,
          // Mirrors how the dashboards page loads itself, so a reload behaves
          // like a navigation to the same view.
          refresh: async () => {
            await store.fetchViews();

            const viewToken = route.params.viewToken as string | undefined;
            if (viewToken) await store.loadView(viewToken);
            else await store.loadDefaultView();
          },
        };
      },
    },
    {
      routes: ["SurveyQuestions"],
      touchedBy: (tool) => tool.includes("section"),
      // A card can move between sections, so the cards the survey store
      // refetches matter here too.
      standalone: false,
      load: async () => {
        const { useSectionsStore } = await import("@stores/survey/sections");
        const store = useSectionsStore();

        return { hasUnsavedChanges: () => store.hasUnsavedChanges, refresh: () => store.fetch() };
      },
    },
    {
      routes: ["AdTemplateEditor"],
      touchedBy: (tool) => tool.includes("ad_template"),
      standalone: true,
      load: async () => {
        const { useAdTemplatesStore } = await import("@stores/adTemplates");
        const store = useAdTemplatesStore();

        return {
          hasUnsavedChanges: () => store.hasUnsavedChanges,
          refresh: async () => {
            await store.fetchTemplate(route.params.token as string);
          },
        };
      },
    },
    {
      routes: ["ReportV2", "ReportV2Settings", "ReportV2Slides", "ReportV2Edit"],
      touchedBy: (tool) => tool.includes("slide"),
      // Slides are not broadcast while the tools run, so this refetch is the
      // only thing that makes a generated report appear.
      standalone: true,
      load: async () => {
        const { useReportsV2Store } = await import("@stores/survey/reportsV2/reportsV2");
        const store = useReportsV2Store();

        return {
          hasUnsavedChanges: () => store.hasUnsavedChanges,
          refresh: () => store.fetchReport(route.params.reportToken as string),
        };
      },
    },
  ];

  async function staleSideStores(mutatedTools: string[]) {
    const displayed = SIDE_STORES.filter(
      (entry) => entry.routes.includes(String(route.name)) && mutatedTools.some(entry.touchedBy),
    );

    return Promise.all(displayed.map(async (entry) => ({ entry, ...(await entry.load()) })));
  }

  async function refreshAfterWrite(mutatedTools: string[], surveyToken: string | null) {
    const stale = await staleSideStores(mutatedTools);
    if (stale.some((side) => side.hasUnsavedChanges())) return warnUnsaved();

    const surveyStore = useSurveyStore();
    const needsSurvey = surveyToken && !coveredBySideStores(stale, mutatedTools);

    if (needsSurvey) {
      if (surveyStore.hasSurveyChanged || (await unsavedCards())) return warnUnsaved();
      await surveyStore.fetch(surveyToken);
    }

    for (const side of stale) await side.refresh();

    if (needsSurvey || stale.length) notifyReloaded();
  }

  // The survey store's refetch cascades into the cards store, so in-progress
  // card edits are at stake even when no side store is involved. Only the
  // questionnaire page lets the user build cards, so it is loaded only there.
  async function unsavedCards() {
    if (String(route.name) !== "SurveyQuestions") return false;

    const { useCardsStore } = await import("@stores/survey/cards");
    return useCardsStore().hasUnsavedChanges;
  }

  // True when every tool that wrote is handled by a standalone side store,
  // which is what makes skipping the survey refetch safe.
  function coveredBySideStores(stale: Awaited<ReturnType<typeof staleSideStores>>, mutatedTools: string[]) {
    const standalone = stale.filter(({ entry }) => entry.standalone).map(({ entry }) => entry);
    if (standalone.length === 0) return false;

    return mutatedTools.every((tool) => standalone.some((entry) => entry.touchedBy(tool)));
  }

  function notifyReloaded() {
    toast.success("Page updated with the assistant's changes");
  }

  function warnUnsaved() {
    toast.info("Episto AI changed this page — save or discard your edits, then reload to see them");
  }

  return { refreshAfterWrite };
}

export default useAssistantPageRefresh;
